import { useTranslation } from 'react-i18next';

import { DashboardKpiEntry } from '@/features/dashboard/components/dashboard/dashboard-kpi-entry';
import { DashboardProjectCardHeaderContent } from '@/features/dashboard/components/dashboard/dashboard-project-card-header-content';
import { BaseProjectSlideCardProps } from '@/features/dashboard/components/dashboard/dashboard-project-slot-card';
import { KpiAnomaly } from '@/features/insights/api/detect-kpi-anomalies';
import { BlurredContainer } from '@/features/insights/components/blurred-container';
import Anomaly from '@/shared/assets/anomaly.svg?react';
import Warning from '@/shared/assets/warning-triangle.png';
import { Card, CardContent, CardHeader } from '@/shared/components/ui/card';
import { ValueSign } from '@/shared/components/value-sign';
import { formatCompactNumber } from '@/shared/utils/format';
export const DashboardAnomalies = ({
  project,
  latestAnomaly,
  otherAnomalies,
  kpi,
  currentCardIndex,
  onCardIndexChange,
  isPaused,
  onTogglePause,
}: BaseProjectSlideCardProps & {
  latestAnomaly?: KpiAnomaly;
  otherAnomalies: KpiAnomaly[];
}) => {
  const { t } = useTranslation('overview', {
    keyPrefix: 'OverviewPage.kpiCards',
  });
  const deviationPercentage = latestAnomaly?.deviation_percentage;
  const anomalyValue = latestAnomaly?.aggregated_value;

  return (
    <Card variant="informationAlt" className="size-full">
      <CardHeader className="flex flex-row items-start justify-between">
        <DashboardProjectCardHeaderContent
          kpiName={t('anomalies')}
          projectName={project.name}
          currentPageIndex={currentCardIndex}
          onCardIndexChange={onCardIndexChange}
          isPaused={isPaused}
          onTogglePause={onTogglePause}
        />
      </CardHeader>
      <CardContent className="flex h-full flex-1 flex-row justify-center gap-6">
        <div className="flex flex-1 basis-1/4 flex-col">
          <div className="flex flex-[2] items-center justify-center">
            <Anomaly className="max-h-16 w-full" />
          </div>
          <div className="flex-1">
            <div className="mt-6 flex flex-row gap-1.5">
              <img
                src={Warning}
                alt="Warning"
                className="size-[18px] object-contain"
              />
              <span className="text-2xs font-semibold">
                <ValueSign
                  value={deviationPercentage ?? 0}
                  showPositiveSign={true}
                />
                {deviationPercentage
                  ? Math.abs(deviationPercentage).toFixed(1) + '%'
                  : '-'}
              </span>
            </div>
            <div className="mt-4 flex flex-1 flex-row items-center gap-2">
              <span className="text-3xl font-semibold leading-none">
                {anomalyValue ? formatCompactNumber(anomalyValue) : '-'}
              </span>
              <div className="flex flex-col gap-1">
                <span className="text-3xs leading-none">{t('anomaly')}</span>
                <span className="text-3xs leading-none">{kpi?.unit ?? ''}</span>
              </div>
            </div>
          </div>
        </div>
        <BlurredContainer
          className="h-full flex-[3] shrink-0"
          isBlurred={otherAnomalies.length === 0}
        >
          <div className="flex h-full flex-col justify-between rounded-sm bg-primary-shade-grey-500/5 p-4">
            {otherAnomalies.map((anomaly, index) => {
              const anomalyKpi = kpi;
              const deviation = anomaly.deviation_percentage ?? 0;
              return (
                anomalyKpi && (
                  <div
                    key={`${anomaly.period}-${index}`}
                    className="flex flex-row gap-3"
                  >
                    <DashboardKpiEntry
                      kpiName={anomalyKpi.name}
                      kpiUnit={anomalyKpi.unit ?? ''}
                      changePercentage={deviation}
                      showTendencyIndicator={false}
                      logoUrl={anomaly.org_picture_url ?? undefined}
                      orgName={anomaly.org_name ?? undefined}
                    />
                  </div>
                )
              );
            })}
          </div>
        </BlurredContainer>
      </CardContent>
    </Card>
  );
};
